import React, { useState, useEffect } from 'react';
import {
  FaCalendarAlt,
  FaClock,
  FaCheck,
  FaTimes,
  FaEllipsisV,
  FaExclamation,
  FaVideo,
} from 'react-icons/fa';
import { sessionAPI } from '../../services/api';
import { tokenManager } from '../../services/tokenManager';
import { isSessionJoinable, getSessionStatus, shouldShowJoinButton } from '../../utils/sessionTimeUtils';
import SessionDetailModal from './SessionDetailModal';
import ConsultationCompletionForm from './ConsultationCompletionForm';

// Pill Components
const StatusPill = ({ status }) => {
  const map = {
    scheduled: {
      icon: <FaCheck className="text-white text-xs" />,
      bg: 'bg-blue-500',
      text: 'Scheduled',
    },
    completed: {
      icon: <FaCheck className="text-white text-xs" />,
      bg: 'bg-gray-500',
      text: 'Completed',
    },
    missed: {
      icon: <FaTimes className="text-white text-xs" />,
      bg: 'bg-red-500',
      text: 'Missed',
    },
    cancelled: {
      icon: <FaTimes className="text-white text-xs" />,
      bg: 'bg-red-500',
      text: 'Cancelled',
    },
    in_progress: {
      icon: <FaExclamation className="text-white text-xs" />,
      bg: 'bg-yellow-500',
      text: 'In Progress',
    },
  };
  const { icon, bg, text } = map[status] || map.scheduled;
  return (
    <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
      <span className={`rounded-full p-1 ${bg}`}>{icon}</span>
      {text}
    </span>
  );
};

const AssessmentPill = ({ status }) => {
  if (status === 'pending') {
    return (
      <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
        <span className="rounded-full p-1 bg-yellow-500">
          <FaExclamation className="text-white text-xs" />
        </span>
        Assessment Pending
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1 text-sm font-medium text-gray-800">
      <span className="rounded-full p-1 bg-blue-500">
        <FaCheck className="text-white text-xs" />
      </span>
      Assessment Complete
    </span>
  );
};

const Sessions = () => {
  const [active, setActive] = useState('Today');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalSessions: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('upcoming');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [updatingSession, setUpdatingSession] = useState(null);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [selectedSessionForCompletion, setSelectedSessionForCompletion] = useState(null);

  const options = ['Today', 'This Week', 'This Month'];

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle session start
  const handleStartSession = async (sessionId, session) => {
    try {
      setUpdatingSession(sessionId);

      // Update session status to in_progress
      await sessionAPI.updateSessionStatus(sessionId, 'in_progress');

      // If session has Zoom URL, open it in new tab
      if (session.zoomStartUrl) {
        window.open(session.zoomStartUrl, '_blank', 'noopener,noreferrer');
      }

      // Refresh the sessions to show updated status
      await fetchSessions();

    } catch (error) {
      console.error('Failed to start session:', error);
      alert('Failed to start session. Please try again.');
    } finally {
      setUpdatingSession(null);
    }
  };

  // Handle session completion (old method - replaced by completion form)
  const handleCompleteSession = async (sessionId) => {
    try {
      setUpdatingSession(sessionId);

      // Update session status to completed
      await sessionAPI.updateSessionStatus(sessionId, 'completed');

      // Refresh the sessions
      await fetchSessions();

    } catch (error) {
      console.error('Failed to complete session:', error);
      alert('Failed to complete session. Please try again.');
    } finally {
      setUpdatingSession(null);
    }
  };

  // Handle opening consultation completion form
  const handleOpenCompletionForm = (session) => {
    setSelectedSessionForCompletion(session);
    setShowCompletionForm(true);
  };

  // Handle consultation completion form submission
  const handleConsultationComplete = async (formData) => {
    try {
      await sessionAPI.completeConsultation(formData.sessionId, formData);
      
      alert('Consultation completed successfully! Medicare receipt has been generated and sent to the patient.');
      
      // Close the form and refresh sessions
      setShowCompletionForm(false);
      setSelectedSessionForCompletion(null);
      await fetchSessions();
      
    } catch (error) {
      console.error('Failed to complete consultation:', error);
      alert('Failed to complete consultation. Please try again.');
    }
  };

  // Helper function to get profile image URL
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    
    if (imagePath.startsWith('https')) {
      return imagePath;
    }
    
    const baseUrl = 'https://backend.adhdrapidcare.com';
    
    if (imagePath.startsWith('/')) {
      return `${baseUrl}${imagePath}`;
    }
    
    return `${baseUrl}/${imagePath}`;
  };

  // Helper function to format date
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Fetch sessions
  const fetchSessions = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const user = tokenManager.getUser();
      if (!user || !user.id) {
        throw new Error('User not authenticated');
      }

      const filters = {
        page,
        limit: 9
      };

      // Add date filters based on active option
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      
      if (active === 'Today') {
        filters.dateFrom = todayStr;
        filters.dateTo = todayStr;
      } else if (active === 'This Week') {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        filters.dateFrom = startOfWeek.toISOString().split('T')[0];
        filters.dateTo = endOfWeek.toISOString().split('T')[0];
      } else if (active === 'This Month') {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        
        filters.dateFrom = startOfMonth.toISOString().split('T')[0];
        filters.dateTo = endOfMonth.toISOString().split('T')[0];
      }

      if (statusFilter) {
        filters.status = statusFilter;
      }

      // Add search filter
      if (debouncedSearchTerm.trim()) {
        filters.patientName = debouncedSearchTerm.trim();
      }

      // Add sort parameters
      if (sortBy === 'name') {
        filters.sortBy = 'patient.name';
        filters.sortOrder = 'asc';
      } else if (sortBy === 'recent') {
        filters.sortBy = 'date';
        filters.sortOrder = 'desc';
      } else if (sortBy === 'upcoming') {
        filters.sortBy = 'date';
        filters.sortOrder = 'asc';
      }

      console.log('Fetching sessions with filters:', filters);
      let response;
      
      try {
        response = await sessionAPI.getSessionsByDoctorId(user.id, filters);
        console.log('API Response:', response);
        console.log('Response type:', typeof response);
        console.log('Response keys:', Object.keys(response || {}));
      } catch (error) {
        console.log('Main endpoint failed, trying fallback...');
        // Try fallback endpoint
        response = await sessionAPI.getTodaySessionsByDoctorId(user.id);
        console.log('Fallback API Response:', response);
      }
      
      // Check if response exists and has the expected structure
      if (!response) {
        throw new Error('No response received from server');
      }

      // Handle different response structures
      let sessionsArray = [];
      if (response.success && response.data && response.data.sessions) {
        sessionsArray = response.data.sessions;
        setPagination(response.data.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalSessions: response.data.sessions.length,
          hasNextPage: false,
          hasPrevPage: false
        });
      } else if (response.sessions) {
        sessionsArray = response.sessions;
        setPagination(response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalSessions: response.sessions.length,
          hasNextPage: false,
          hasPrevPage: false
        });
      } else if (Array.isArray(response)) {
        sessionsArray = response;
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalSessions: response.length,
          hasNextPage: false,
          hasPrevPage: false
        });
      } else {
        console.error('Unexpected response structure:', response);
        throw new Error('Invalid response structure from server');
      }

      // Add this sorting logic:
      if (sortBy === 'name') {
        sessionsArray = sessionsArray.slice().sort((a, b) => {
          const nameA = (a.patient?.name || '').toLowerCase();
          const nameB = (b.patient?.name || '').toLowerCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
      }

      setSessions(sessionsArray);
    } catch (err) {
      console.error('Error fetching sessions:', err);
      setError(err.message || 'Failed to fetch sessions');
      // Set empty sessions and default pagination on error
      setSessions([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalSessions: 0,
        hasNextPage: false,
        hasPrevPage: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchSessions();
  }, [active, debouncedSearchTerm, statusFilter, sortBy]);

  // Handle filter change
  const handleFilterChange = (filter) => {
    setActive(filter);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle pagination
  const handlePageChange = (page) => {
    fetchSessions(page);
  };

  // Handle search
  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // Handle sort change
  const handleSortChange = (sortValue) => {
    setSortBy(sortValue);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  if (error) {
    return (
      <div className="">
        <div className="mb-6 md:mb-8">
          <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Sessions</h1>
          <p className="text-gray-600 mt-2">
            Track your session history, progress, and upcoming appointments in one place.
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => fetchSessions()}
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-xl md:text-2xl font-semibold text-gray-800">Sessions</h1>
        <p className="text-gray-600 mt-2">
          Track your session history, progress, and upcoming appointments in one place.
        </p>
        
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between mb-6 gap-4">
        {/* Date Filters */}
        <div className="inline-flex items-center gap-2 p-1 border border-gray-300 rounded-md bg-white">
          {options.map((option) => (
            <button
              key={option}
              onClick={() => handleFilterChange(option)}
              className={`px-3 py-1 rounded-md text-xs md:text-sm font-medium transition-all duration-200
                ${active === option
                  ? 'bg-blue-500 text-white shadow-sm'
                  : 'text-gray-600 hover:text-blue-600 hover:bg-gray-100'}`}
            >
              {option}
            </button>
          ))}
        </div>

        {/* Search and Dropdowns */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative">
            <input
              type="text"
              placeholder="Search by patient name"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-60 pl-10 pr-10 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              {loading && searchTerm ? (
                <svg className="w-4 h-4 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              )}
            </div>
            {searchTerm && !loading && (
              <button
                onClick={() => handleSearch('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filters */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="">All Status</option>
            <option value="scheduled">Scheduled</option>
            <option value="in_progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => handleSortChange(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          >
            <option value="upcoming">Sort by: Upcoming</option>
            <option value="recent">Sort by: Recent</option>
            <option value="name">Sort by: Name</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white p-4 rounded-xl shadow-sm border animate-pulse">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div>
                    <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded w-16"></div>
                  </div>
                </div>
                <div className="h-6 bg-gray-200 rounded w-12"></div>
              </div>
              <div className="space-y-2">
                <div className="h-3 bg-gray-200 rounded w-full"></div>
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
      {/* Session Cards */}
          {sessions.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {sessions.map((session) => (
                <div key={session.id || session._id} className="bg-white p-4 rounded-xl shadow-sm border">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <img
                        src={getProfileImageUrl(session.patient?.profileImage)}
                  alt="Avatar"
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div>
                        <h4 className="font-semibold text-sm text-gray-800">{session.patient?.name || 'Unknown Patient'}</h4>
                        <p className="text-xs text-gray-500">{session.patient?.email || 'No email'}</p>
                </div>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded ${session.type === 'consultation'
                    ? 'bg-blue-100 text-blue-600'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                      {session.type || 'Session'}
              </span>
            </div>

            {/* Date, Time, Duration */}
            <div className="flex items-center gap-3 text-xs text-gray-500 mb-2">
              <div className="flex items-center gap-1">
                <FaCalendarAlt className="text-gray-400" />
                      <span>{session.date ? formatDate(session.date) : 'No date'}</span>
              </div>
              <div className="flex items-center gap-1">
                <FaClock className="text-gray-400" />
                      <span>{session.startTime ? formatTime(session.startTime) : 'No time'}</span>
              </div>
              <span className="px-2 py-0.5 bg-gray-200 rounded text-gray-600 text-xs">
                      {session.duration || 30} min
              </span>
            </div>

                  {/* Status */}
            <div className="flex justify-between items-center text-sm mb-3">
                    <StatusPill status={session.status || 'scheduled'} />
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2">
              {session.status === 'scheduled' && (
                <>
                  <button className={`bg-blue-500 text-white text-sm px-4 py-2 rounded-md w-full flex items-center justify-center gap-2 ${isSessionJoinable(session) ? 'hover:bg-blue-600' : 'opacity-50 cursor-not-allowed'}`}
                          onClick={() => handleStartSession(session.id || session._id, session)}
                          disabled={!isSessionJoinable(session)}
                        >
                          Join Session
                  </button>
                  <button
                          className="bg-white border border-gray-200 text-gray-700 text-sm px-4 py-2 rounded-md w-full"
                          onClick={() => { setSelectedSessionId(session.id || session._id); setShowSessionModal(true); }}
                        >
                          View Details
                        </button>
                      </>
                    )}
                    {session.status === 'in_progress' && (
                      <>
                        <button
                          onClick={() => handleOpenCompletionForm(session)}
                          className="bg-green-500 text-white text-sm px-4 py-2 rounded-md w-full flex items-center justify-center gap-2 hover:bg-green-600"
                        >
                          Complete Consultation
                        </button>
                        <button
                          className="bg-white border border-gray-200 text-gray-700 text-sm px-4 py-2 rounded-md w-full"
                          onClick={() => { setSelectedSessionId(session.id || session._id); setShowSessionModal(true); }}
                        >
                    View Details
                  </button>
                </>
              )}
                    {(session.status === 'completed' || session.status === 'cancelled') && (
                      <button className="bg-white text-gray-700 border border-gray-200 text-sm px-4 py-2 rounded-md w-full"
                        onClick={() => { setSelectedSessionId(session.id || session._id); setShowSessionModal(true); }}
                      >
                  View Details
                </button>
              )}
              <button className="border border-gray-200 px-2 py-2 rounded-md">
                <FaEllipsisV className="text-gray-400 text-sm" />
              </button>
            </div>

                  {/* Join Session Button - Always visible but conditionally enabled */}
                  {/* {session.zoomJoinUrl && session.status !== 'completed' && (
                    <div className="mt-2">
                      <a
                        href={session.zoomJoinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-center w-full text-sm px-4 py-2 rounded-md ${isSessionJoinable(session)
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed pointer-events-none'
                          }`}
                        tabIndex={isSessionJoinable(session) ? 0 : -1}
                        aria-disabled={!isSessionJoinable(session)}
                      >
                        Join Session
                      </a>
                    </div>
                  )} */}

          </div>
        ))}
      </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
              </svg>
              <h2 className="text-lg font-medium text-gray-700">No sessions found</h2>
              <p className="text-sm text-gray-500 mt-1">No sessions match your current filters.</p>
            </div>
          )}

      {/* Pagination */}
          {sessions.length > 0 && (
      <div className="flex flex-wrap justify-between items-center mt-6 gap-4">
              <p className="text-sm text-gray-500">
                Showing {((pagination.currentPage - 1) * 9) + 1}–{Math.min(pagination.currentPage * 9, pagination.totalSessions)} of {pagination.totalSessions} sessions
              </p>
        <nav className="inline-flex rounded-md shadow-sm">
                <button 
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
            <span className="sr-only">Previous</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" />
            </svg>
          </button>
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  const isActive = pageNum === pagination.currentPage;

                  return (
            <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-4 py-2 border text-sm font-medium ${isActive
                          ? 'bg-blue-50 border-blue-500 text-blue-600'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
            </button>
                  );
                })}
                <button 
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
            <span className="sr-only">Next</span>
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" />
            </svg>
          </button>
        </nav>
      </div>
          )}
        </>
      )}

      <SessionDetailModal
        sessionId={selectedSessionId}
        open={showSessionModal}
        onClose={() => setShowSessionModal(false)}
      />
      
      <ConsultationCompletionForm
        session={selectedSessionForCompletion}
        open={showCompletionForm}
        onClose={() => {
          setShowCompletionForm(false);
          setSelectedSessionForCompletion(null);
        }}
        onSubmit={handleConsultationComplete}
      />
    </>
  );
};

export default Sessions;
